import React, { useState, useEffect, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { API_BASE } from '../../config/environments/api';
import './CleanCookingPage.scss';

const CleanCookingPage = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch clean cooking data from API
  useEffect(() => {
    const fetchCleanCookingData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const apiUrl = `${API_BASE}/clean-cooking/clean-cooking-avg-year-wise`;
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success && result.data && Array.isArray(result.data)) {
          // Sort data by year to ensure proper order and ensure year is a number
          const sortedData = result.data
            .map(item => ({
              ...item,
              year: typeof item.year === 'string' ? parseInt(item.year) : item.year,
              avgValue: typeof item.avgValue === 'string' ? parseFloat(item.avgValue) : item.avgValue
            }))
            .sort((a, b) => a.year - b.year);
          
          setChartData(sortedData);
        } else {
          setError(result.message || 'No data available');
          setChartData([]);
        }
      } catch (err) {
        setError(`Failed to fetch clean cooking data: ${err.message}`);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCleanCookingData();
  }, []);

  // Prepare chart data
  const chartOption = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return {
        title: {
          text: 'No Data Available',
          left: 'center',
          top: 'middle',
          textStyle: {
            color: '#999',
            fontSize: 16,
            fontFamily: "'Poppins', sans-serif"
          }
        },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [{ type: 'bar', data: [] }]
      };
    }

    const years = chartData.map(item => String(item.year || ''));
    const avgValues = chartData.map(item => {
      const value = Number(item.avgValue);
      return isNaN(value) ? 0 : value;
    });


    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'white',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        borderRadius: 8,
        padding: [10, 15],
        textStyle: {
          color: '#666',
          fontSize: 12,
          fontFamily: "'Poppins', sans-serif",
          fontWeight: '500'
        },
        formatter: function (params) {
          let result = `<div style="padding: 5px; background: white; border-radius: 8px; font-family: 'Poppins', sans-serif;">`;
          result += `<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 8px; font-family: 'Poppins', sans-serif;">${params[0].axisValue}</div>`;
          params.forEach((param) => {
            result += `<div style="color: #666; font-size: 14px; font-weight: 500; margin-bottom: 4px; font-family: 'Poppins', sans-serif;">`;
            result += `<span style="display: inline-block; width: 10px; height: 10px; background: ${param.color}; border-radius: 2px; margin-right: 8px;"></span>`;
            result += `${param.seriesName}: <span style="font-weight: 600; color: #333;">${param.value}%</span>`;
            result += `</div>`;
          });
          result += `</div>`;
          return result;
        }
      },
      grid: {
        left: '5%',
        right: '5%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 12,
          fontFamily: "'Poppins', sans-serif"
        }
      },
      yAxis: {
        type: 'value',
        name: 'Clean Cooking Access in (%)',
        nameLocation: 'middle',
        nameGap: 50,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 12,
          fontFamily: "'Poppins', sans-serif",
          formatter: function (value) {
            return value + '%';
          }
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 12,
          fontFamily: "'Poppins', sans-serif"
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Access to Clean Cooking',
          type: 'bar',
          data: avgValues,
          itemStyle: {
            color: '#D48019'
          },
          barWidth: '40%',
          label: {
            show: true,
            position: 'top',
            color: '#666',
            fontSize: 12,
            fontFamily: "'Poppins', sans-serif",
            formatter: function (params) {
              return params.value.toFixed(1) + '%';
            }
          }
        }
      ],
      media: [
        {
          query: { maxWidth: 1024 },
          option: {
            grid: {
              left: '8%',
              right: '8%',
              bottom: '20%',
              top: '12%'
            },
            xAxis: {
              axisLabel: {
                fontSize: 12
              }
            },
            yAxis: {
              axisLabel: {
                fontSize: 12
              },
              nameTextStyle: {
                fontSize: 12
              }
            },
            series: [
              {
                barWidth: '50%',
                label: {
                  fontSize: 12
                }
              }
            ]
          }
        },
        {
          query: { maxWidth: 768 },
          option: {
            grid: {
              left: '10%',
              right: '10%',
              bottom: '15%',
              top: '15%'
            },
            xAxis: {
              axisLabel: {
                fontSize: 12
              }
            },
            yAxis: {
              axisLabel: {
                fontSize: 12
              },
              nameTextStyle: {
                fontSize: 12
              }
            },
            series: [
              {
                barWidth: '45%',
                label: {
                  fontSize: 12,
                  show: false
                }
              }
            ]
          }
        }
      ]
    };
  }, [chartData]);

  return (
    <div className="clean-cooking-page">
      <div className="clean-cooking-container">
        <div className="section-header">
          {/* <h1 className="section-title">Access Clean Cooking <span style={{color:'#D48019'}}>Africa</span></h1> */}
          <p className="section-subtitle">
            Tracking average percentage of population with access to clean cooking fuels over time
          </p>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner">Loading...</div>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">{error}</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="no-data-container">
            <p className="no-data-message">No data available</p>
          </div>
        ) : (
          <div className="chart-wrapper">
            {chartData.length > 0 && (
              <ReactECharts
                option={chartOption}
                style={{ height: '450px', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CleanCookingPage;

